/**
 * Password-change: business-error model.
 *
 * The interesting work here is not classifying the failure, it is deciding
 * WHICH FIELD carries it. The backend answers with a status that already says
 * so, and every message it sends is user-facing copy written for this form
 * (`backend/src/features/auth/application/use-cases/ChangeMyPassword.ts`):
 *
 * - `401`: "Your current password is incorrect." → the current-password field.
 *   (Also covers "Your session is no longer valid", which is rarer; showing it
 *   on the same field is harmless, and the api client's refresh has already run.)
 * - `400`: the three rules that need server-side knowledge: the length policy,
 *   "must be different from your current one", and the breached-password
 *   screening → the new-password field.
 * - anything else (403 archived, 429 rate limit, 5xx, offline) → a snackbar.
 *   Attaching those to a field would tell the user to fix an input that is fine.
 *
 * Re-using the backend's message verbatim is deliberate: rewriting it here would
 * create a second copy to keep in sync, and the server's wording is the one the
 * security review looked at.
 */
import { classifyInfraError, type InfraFailure } from '@/utils/infra-error';

import type { PasswordValidationErrors } from './password-change.model';

export type PasswordChangeError =
  | InfraFailure
  | { kind: 'validation'; fields: PasswordValidationErrors };

const FALLBACK_MESSAGE = 'Failed to change your password.';

/**
 * Map an unknown thrown error into the form's error model. Statuses the form can
 * act on become field errors; everything else stays a coarse infra failure the
 * page surfaces as a snackbar.
 */
export function toPasswordChangeError(e: unknown): PasswordChangeError {
  const failure = classifyInfraError(e, FALLBACK_MESSAGE);
  if (failure.kind !== 'api') return failure;

  if (failure.status === 401) {
    return { kind: 'validation', fields: { currentPassword: failure.message } };
  }
  if (failure.status === 400) {
    return { kind: 'validation', fields: { newPassword: failure.message } };
  }
  return failure;
}
